import {
  AnalyticsPageAttributes,
  BossOrganization,
  ContextData,
  Program,
  Registration,
  RequestDetails,
  Signup,
} from '../context-data';
import {Persona} from '../../../data/model';
import {Address} from '../../../data/model';

const stringIsValid = (value: unknown): boolean => typeof value === 'string';
const numberIsValid = (value: unknown): boolean => typeof value === 'number';
const arrayIsValid = (
  value: unknown,
  validator: (item: unknown) => boolean,
): boolean => Array.isArray(value) && value.every(validator);
export const dateStringIsValid = (value: unknown): boolean => {
  let date: Date;
  if (typeof value === 'object') {
    date = new Date((value as object)?.toString());
  } else if (typeof value === 'string') {
    date = new Date(value);
  } else {
    return false;
  }
  return !isNaN(date.getTime());
};

const bossOrganizationIsValid = (value: BossOrganization): boolean =>
  value &&
  numberIsValid(value.id) &&
  stringIsValid(value.name) &&
  (!value.orgLogo || stringIsValid(value.orgLogo));

const programIsValid = (value: Program | undefined): boolean =>
  !!value &&
  numberIsValid(value.id) &&
  stringIsValid(value.system) &&
  stringIsValid(value.type) &&
  stringIsValid(value.name);

const registrationIsValid = (value: Registration | undefined): boolean =>
  !!value &&
  numberIsValid(value.id) &&
  stringIsValid(value.system) &&
  stringIsValid(value.type);

const personaIsValid = (value: Persona): boolean =>
  value &&
  (!value.id || numberIsValid(value.id)) &&
  (!value.system || stringIsValid(value.system)) &&
  (!value.type || stringIsValid(value.type)) &&
  (!value.firstName || stringIsValid(value.firstName)) &&
  (!value.lastName || stringIsValid(value.lastName)) &&
  (!value.address || addressIsValid(value.address)) &&
  (!value.dob || dateStringIsValid(value.dob)) &&
  (!value.email || stringIsValid(value.email)) &&
  (!value.phoneNumber || stringIsValid(value.phoneNumber));

const participantIsValid = (value: Persona): boolean =>
  personaIsValid(value) && (!value.gender || stringIsValid(value.gender));

const requestDetailsAreValid = (value: RequestDetails): boolean =>
  value &&
  (!value.eventType || stringIsValid(value.eventType)) &&
  (!value.eventCost || numberIsValid(value.eventCost)) &&
  (!value.programStartDate || dateStringIsValid(value.programStartDate)) &&
  (!value.programEndDate || dateStringIsValid(value.programEndDate)) &&
  (!value.paymentDate || dateStringIsValid(value.paymentDate)) &&
  (!value.sport || stringIsValid(value.sport)) &&
  (!value.submittedBy || stringIsValid(value.submittedBy));

const signUpIsValid = (value: Signup): boolean =>
  value &&
  (!value.program || programIsValid(value.program)) &&
  (!value.registration || registrationIsValid(value.registration)) &&
  participantIsValid(value.participant) &&
  requestDetailsAreValid(value.requestDetails);

const signUpArrayIsValid = (value: Signup[]): boolean =>
  Array.isArray(value) && value.every(signUpIsValid);

const addressIsValid = (value: Address | undefined): boolean =>
  !!value &&
  (!value.address1 || stringIsValid(value.address1)) &&
  (!value.address2 || stringIsValid(value.address2)) &&
  (!value.state || stringIsValid(value.state)) &&
  (!value.city || stringIsValid(value.city)) &&
  (!value.zip || stringIsValid(value.zip)) &&
  (!value.country || stringIsValid(value.country));

const registrantIsValid = (value: Persona): boolean => personaIsValid(value);

const analyticsPageAttributesAreValid = (
  value: AnalyticsPageAttributes,
): boolean =>
  value &&
  stringIsValid(value.depth1) &&
  stringIsValid(value.depth2) &&
  stringIsValid(value.depth3) &&
  (!value.depth4 || stringIsValid(value.depth4)) &&
  (!value.surveySport || stringIsValid(value.surveySport)) &&
  stringIsValid(value.surveyType);

export const contextBlobIsValid = (data: ContextData): boolean => {
  return (
    data &&
    arrayIsValid(data.enabledSalesChannels, stringIsValid) &&
    bossOrganizationIsValid(data.bossOrganization) &&
    signUpArrayIsValid(data.signups) &&
    registrantIsValid(data.registrant) &&
    (!data.guestKey || stringIsValid(data.guestKey)) &&
    analyticsPageAttributesAreValid(data.analyticsPageAttributes)
  );
};
